import React, { useEffect, useRef, useState } from 'react';
import Icon from '../Icon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { useTranslation } from '../../i18n';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeChoice } from '../../hooks/useThemeChoice';
import { User } from '../../types/auth';
import { sitePageUrl } from '../../utils/siteLinks';
import './AccountMenu.css';

interface AccountMenuProps {
  user: User | null;
  onLogout: () => void;
  onOpenSuggestion: () => void;
  /** Where the popover opens from the avatar: below it (sidebar) or above. */
  placement?: 'below' | 'above';
}

/**
 * The avatar and its menu (Faz 23, T-180): Ayarlar, feedback, contact, theme, language and
 * sign out. Profile is part of Ayarlar. Escape or a click outside closes it.
 */
const AccountMenu: React.FC<AccountMenuProps> = ({ user, onLogout, onOpenSuggestion, placement = 'below' }) => {
  const { t, language } = useTranslation();
  const { resolvedTheme } = useTheme();
  const chooseTheme = useThemeChoice();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isAccountMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false);
        accountButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  const runAndClose = (action: () => void) => () => {
    action();
    setIsAccountMenuOpen(false);
  };

  const isDark = resolvedTheme === 'dark';
  const initial = (user?.firstName?.[0] || user?.username?.[0] || 'U').toLocaleUpperCase(language);

  return (
    <div className={`account-menu-anchor account-menu-anchor--${placement}`} ref={accountMenuRef}>
      <button
        ref={accountButtonRef}
        type="button"
        className="account-avatar"
        onClick={() => setIsAccountMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isAccountMenuOpen}
        aria-controls="account-menu"
        aria-label={t('navigation.accountMenu')}
      >
        {initial}
      </button>
            {isAccountMenuOpen && (
              <div id="account-menu" className="account-menu" role="menu" aria-label={t('navigation.accountMenu')}>
                <button
                  type="button"
                  role="menuitem"
                  className="account-menu__item"
                  onClick={runAndClose(() => { window.location.hash = '#/settings'; })}
                >
                  <Icon name="settings" className="account-menu__icon" />
                  {t('navigation.settings')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="account-menu__item"
                  onClick={runAndClose(onOpenSuggestion)}
                >
                  <Icon name="lightbulb" className="account-menu__icon" />
                  {t('navigation.suggestion')}
                </button>
                <a
                  role="menuitem"
                  className="account-menu__item"
                  href={sitePageUrl('contact', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsAccountMenuOpen(false)}
                >
                  <Icon name="mail" className="account-menu__icon" />
                  {t('navigation.contact')}
                  <Icon name="open" className="account-menu__trailing" />
                </a>

                <div className="account-menu__divider" role="separator" />

                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={isDark}
                  className="account-menu__item"
                  onClick={() => chooseTheme(isDark ? 'light' : 'dark')}
                >
                  <Icon name={isDark ? 'moon' : 'sun'} className="account-menu__icon" />
                  {t('navigation.theme')}
                  <span className="account-menu__trailing">
                    {isDark ? t('profile.theme.dark') : t('profile.theme.light')}
                  </span>
                </button>
                <div className="account-menu__row">
                  <span className="account-menu__row-label">
                    <Icon name="globe" className="account-menu__icon" />
                    {t('navigation.language')}
                  </span>
                  <LanguageSwitcher variant="surface" />
                </div>

                <div className="account-menu__divider" role="separator" />

                <button
                  type="button"
                  role="menuitem"
                  className="account-menu__item is-danger"
                  onClick={runAndClose(onLogout)}
                >
                  <Icon name="logout" className="account-menu__icon" />
                  {t('navigation.logout')}
                </button>
              </div>
            )}
    </div>
  );
};

export default AccountMenu;
